import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router";
import { Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import moment from "moment";
import axios from "axios";
import Lottie from "react-lottie";
import { LocaleContext } from "../../../routing/LangRouter";
import animationData from "../../../assets/lotties/Arrow_Plan.json";

//import utils
import { formatPersianDate } from "../../../utils/date";
import { numberWithCommas } from "../../../utils/utility";

//import constans
import BASE_URL from "../../../constants/baseUrls";
import { IS_MOBILE } from "../../../constants/breakpoints";
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";

//import customized hooks
import useCityList from "../../../hooks/useCityList";

//import general components
import Loading from "../loading/loading";
import BaseDialog from "../base-dialog/base-dialog";
import EmptyOrErrorState from "../empty-or-error-state/empty-or-error-state";

//import images and icons
import noImage from "../../../assets/imgs/location.png";
import { InfoCircle, Warning2 } from "iconsax-react";

//import styles
import "./without-modal.scss";

import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";

function WithoutDestinationDateModal({ open, setOpen, setRequestFlag }) {
    //general variables
    const navigate = useNavigate();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");
    const { routes, setRoutes } = useContext(ItineraryContext);

    //lotties
    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: animationData,
        rendererSettings: {
            preserveAspectRatio: "xMidYMid slice",
        },
    };

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const intFlightInformation = JSON.parse(sessionStorage.getItem("intFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isTicket, isAccommodation, isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};
    const favorites = JSON.parse(sessionStorage.getItem("favorites"));

    const [information, setInformation] = useState();
    useEffect(() => {
        if (isTicket) {
            setInformation(domesticFlightInformation);
            setInformation(intFlightInformation);
        }
        if (!isTicket && isAccommodation) {
            setInformation(domesticFlightInformation);
            setInformation(intFlightInformation);
        }
        if (!isTicket && !isAccommodation) {
            setInformation(itineraryInformation);
        }
    }, []);

    //handle close modal
    const handleClose = () => {
        setOpen(false);
        navigate(`/${locale}`, { state: { scrollToTravelForm: true } });
    };

    //without destination request
    const [loadingRecommender, setLoadingRecommender] = useState(false);
    const [recommenderList, setRecommenderList] = useState();
    const [error, setError] = useState(false);
    const [isEmptyList, setIsEmptyList] = useState(false);
    const getRecommenderList = async () => {
        try {
            setLoadingRecommender(true);
            setError(false);
            const data = {
                favourites: favorites,
                travelers: {
                    adults_count: information?.personCounter.adult,
                    children_count: information?.personCounter.child,
                    infants_count: information?.personCounter.infant,
                },
                user_id: accessToken ? JSON.parse(localStorage.getItem("userInformation")).id : 0,
                from_city_id: information.departure.id,
            };

            const response = await axios.post(`${BASE_URL}/itinerary/full_recommender/`, data, {
                params: {
                    is_cake: false,
                },
            });
            setRecommenderList(
                Object.values(response.data.data)
                    ?.filter(item => !!item.time_ranges.length)
                    .sort((a, b) => b.city_data.total_score - a.city_data.total_score),
            );
            setIsEmptyList(!response.data.data?.time_ranges?.length ? true : false);
        } catch {
            setError(true);
        } finally {
            setLoadingRecommender(false);
        }
    };

    useEffect(() => {
        if (!!information && !information?.destination && !information?.departureDate && !information?.returnDate) {
            getRecommenderList();
        }
    }, [information]);

    const [minPriceList, setMinPriceList] = useState([]);
    useEffect(() => {
        recommenderList?.forEach(recommenderItem => {
            let sumPriceList = [];
            recommenderItem?.time_ranges.forEach(time => {
                sumPriceList.push(time.total_price);
            });
            setMinPriceList(values => [...values, Math.min(...sumPriceList)]);
        });
    }, [recommenderList]);

    //request city list. inputs:foreign, all, flight, accommodation, bus
    const { getCities } = useCityList(false, true, false, false, true, false); //when the (all) parameter is set to true, it ignores the value of foreign

    //handle select destination
    const handleSelectedDate = (city, date) => {
        getCities(city.name).then(cityList => {
            const findCity = cityList.find(cityItem => cityItem.id === city.id);
            domesticFlightInformation.destination = findCity;
            domesticFlightInformation.departureDate = date.departure_date;
            domesticFlightInformation.returnDate = date.return_date;
            sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));

            intFlightInformation.destination = findCity;
            intFlightInformation.departureDate = date.departure_date;
            intFlightInformation.returnDate = date.return_date;
            sessionStorage.setItem("intFlightInformation", JSON.stringify(intFlightInformation));

            hotelInformation.destination = findCity;
            hotelInformation.checkIn = date.departure_date;
            hotelInformation.checkOut = date.return_date;
            sessionStorage.setItem("hotelInformation", JSON.stringify(hotelInformation));

            itineraryInformation.destination = findCity;
            itineraryInformation.departureDate = date.departure_date;
            itineraryInformation.returnDate = date.return_date;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));

            //skip the hotel step in the flow of itinerary when the city has flight but doesn't have accommodation
            if (findCity?.flight && !findCity?.accommodation) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation: false,
                    isItinerary,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
            }

            if (!findCity?.flight) {
                if (findCity?.accommodation) {
                    //skip the flight step in the flow of itinerary when the city doesn't have flight but have accommodation
                    const newGeneralInformation = {
                        isTicket: false,
                        isAccommodation,
                        isItinerary,
                    };
                    sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                    setRoutes([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]);
                    sessionStorage.setItem("visitedRoutes", JSON.stringify([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]));
                    navigate(`/${locale}/itinerary/hotel`);
                } else {
                    //skip both flight and accommodation steps when the city doesn't have flight and accommodation
                    const newGeneralInformation = {
                        isTicket: false,
                        isAccommodation: false,
                        isItinerary,
                    };
                    sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                    setRoutes([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]);
                    sessionStorage.setItem(
                        "visitedRoutes",
                        JSON.stringify([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]),
                    );
                    navigate(`/${locale}/itinerary/itinerary-page`);
                }
            }

            //skip both flight and accommodation steps when the user doesn't select flight and goes to the hotel page and the selected city doesn't have accommodation
            if (!isTicket && !findCity?.accommodation) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation: false,
                    isItinerary,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                setRoutes([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]);
                sessionStorage.setItem("visitedRoutes", JSON.stringify([`/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]));
                navigate(`/${locale}/itinerary/itinerary-page`);
            }

            //when the selected country is international
            if (findCity?.country.id !== 1) {
                const newGeneralInformation = {
                    isTicket,
                    isAccommodation,
                    isItinerary,
                    isHotelInternational: true,
                    isFlightInternational: true,
                };
                sessionStorage.setItem("generalInformation", JSON.stringify(newGeneralInformation));
                const { isFlightInternational, isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

                if (isFlightInternational) {
                    setRoutes([...routes, `/${locale}/itinerary/international-flight`]);
                    sessionStorage.setItem(
                        "visitedRoutes",
                        JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/international-flight`]),
                    );
                    navigate(`/${locale}/itinerary/international-flight`);
                }

                //when the modal opens in the hotel page for when the user just select I want accommodation
                if (!isTicket && isHotelInternational) {
                    setRoutes([...routes, `/${locale}/itinerary/hotel`]);
                    sessionStorage.setItem(
                        "visitedRoutes",
                        JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]),
                    );
                    navigate(`/${locale}/itinerary/hotel`);
                }
            }

            setOpen(false);
            if (location.pathname !== `/${locale}/itinerary/hotel`) {
                setRequestFlag(true);
            }
        });
    };

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: undefined,
        });
    };

    return (
        <BaseDialog title={`${t("suggesteDestinationsDate")}`} open={open} handleClose={handleClose} maxWidth="xl" top="0" icon="back">
            <Grid container className="without-modal" item xs={12} rowGap={2} justifyContent="center" alignItems="flex-start" p={1}>
                {loadingRecommender && <Loading />}

                <Grid container item xs={12} rowGap={2} columnGap={1} justifyContent={{ xs: "space-between", md: "flex-start" }}>
                    <Swiper
                        dir={locale === "en" ? "ltr" : "rtl"}
                        direction={IS_MOBILE ? "horizontal" : "vertical"}
                        slidesPerView={IS_MOBILE ? 1 : "auto"}
                        spaceBetween={30}
                        className="mySwiper"
                        autoHeight
                    >
                        {error ? (
                            <Grid container item xs={11} justifyContent="center" m="auto" mt={2}>
                                <EmptyOrErrorState
                                    title={t("errorMessage")}
                                    icon={<Warning2 size="32" color="var(--red-color)" variant="Bold" />}
                                    actionLabel={t("retry")}
                                    handleClick={getRecommenderList}
                                />
                            </Grid>
                        ) : recommenderList?.length ? (
                            recommenderList?.map((recommenderItem, index) => {
                                return (
                                    <SwiperSlide>
                                        <Grid container item xs={12} rowGap={2} justifyContent="space-between">
                                            <Grid
                                                className="without-modal__city-recommender full"
                                                container
                                                item
                                                xs={10}
                                                md={1.5}
                                                justifyContent="center"
                                                alignItems="flex-start"
                                                sx={{ backgroundImage: `url(${recommenderItem.city_data.image || noImage})`, cursor: "auto" }}
                                            >
                                                <span className="without-modal__city-name">
                                                    {index + 1}-{" "}
                                                    {locale === "en" ? recommenderItem.city_data.english_name : recommenderItem.city_data.name}
                                                </span>
                                            </Grid>

                                            <Grid
                                                className="without-modal__lottie"
                                                container
                                                item
                                                xs={2}
                                                display={!IS_MOBILE || index === recommenderList?.length - 1 ? "none" : "flex"}
                                                alignItems="center"
                                            >
                                                <div>
                                                    <Lottie options={defaultOptions} />
                                                </div>
                                            </Grid>

                                            <Grid container item xs={10} rowGap={2} columnGap={2} alignItems="center">
                                                {recommenderItem.time_ranges.map(time => {
                                                    return (
                                                        <Grid
                                                            className={`without-modal__recommender-date ${
                                                                minPriceList?.[index] === time?.total_price ? "min-price" : ""
                                                            }`}
                                                            container
                                                            item
                                                            xs={12}
                                                            md={2}
                                                            alignItems="flex-start"
                                                            onClick={() => handleSelectedDate(recommenderItem?.city_data, time)}
                                                        >
                                                            <Grid
                                                                className="without-modal__date-header"
                                                                container
                                                                item
                                                                xs={12}
                                                                justifyContent="space-evenly"
                                                                alignItems="center"
                                                            >
                                                                <span className="without-modal__date">
                                                                    {locale === "en"
                                                                        ? moment(time?.departure_date).format("D MMM")
                                                                        : formatDate(time?.departure_date)}
                                                                </span>
                                                                <span> {t("to-2")} </span>
                                                                <span className="without-modal__date">
                                                                    {locale === "en"
                                                                        ? moment(time?.return_date).format("D MMM")
                                                                        : formatDate(time?.return_date)}
                                                                </span>
                                                            </Grid>

                                                            <Grid className="without-modal__prices" container item xs={12} rowGap={1}>
                                                                <Grid container item xs={12} justifyContent="space-between">
                                                                    <span>{t("ticketPrice")}:</span>
                                                                    <span>
                                                                        {t("from")} {numberWithCommas(time?.total_flight_price)} {t("currency")}
                                                                    </span>
                                                                </Grid>
                                                                <Grid container item xs={12} justifyContent="space-between">
                                                                    <span>{t("hotelPrice")}:</span>
                                                                    <span>
                                                                        {t("from")} {numberWithCommas(time?.total_hotel_price)} {t("currency")}
                                                                    </span>
                                                                </Grid>
                                                                <Divider width="100%" />
                                                                <Grid container item xs={12} justifyContent="space-between">
                                                                    <span>{t("total")}:</span>
                                                                    <span className="without-modal__total-price">
                                                                        {t("from")} {numberWithCommas(time?.total_price)} {t("currency")}
                                                                    </span>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>
                                        </Grid>
                                    </SwiperSlide>
                                );
                            })
                        ) : isEmptyList ? (
                            <Grid container item xs={12} rowGap={2} direction="column" justifyContent="center" alignItems="center">
                                <EmptyOrErrorState
                                    title={t("noSuitableTravelTime")}
                                    icon={<InfoCircle size="32" color="var(--secondary-color)" variant="Bold" />}
                                    actionLabel={t("backToHomePage")}
                                    handleClick={handleClose}
                                />
                            </Grid>
                        ) : (
                            <Grid container item xs={12} rowGap={2} direction="column" justifyContent="center" alignItems="center">
                                <span> {t("lookingForBest")}</span>
                                <span>{t("thisProcessMayTakeTime")}</span>
                            </Grid>
                        )}
                    </Swiper>
                </Grid>
            </Grid>
        </BaseDialog>
    );
}

export default WithoutDestinationDateModal;
